/**
 * Public types for the Configurable Experiences DataSourceManager
 * (child SAD — Data Binding & Behavioral Wiring, ADR-10, §3, §8, §10, §11).
 *
 * The provider owns the fetch lifecycle for every `dataSources` entry in a
 * DSL config:
 *   1. Topologically orders declarations by `dependsOn`.
 *   2. Interpolates request fields against `{ context, formState, ...sources }`
 *      via DB-1.2 (`interpolate`, `interpolateObject`).
 *   3. Validates the resolved endpoint against DB-1.1
 *      (`isEndpointAllowed`) as runtime defense-in-depth.
 *   4. Fetches with timeout / retry / abort, extracts via `responsePath`,
 *      classifies the outcome (`resolved` | `error` | `empty`).
 *   5. Exposes resolved data and a generic `formState`/`updateFormState`
 *      API that drives re-fetches of templates referencing
 *      `{{formState.*}}`.
 *
 * **Base URL resolution (DB-1.3 scope).** For this ticket DSM consumes
 * **full URLs only** — callers must pre-resolve any path into a full URL
 * before passing it as `endpoint`. The ConfigurableStep bridge (DB-1.5)
 * will supply a path→baseUrl resolver in a follow-up. Paths that start
 * with `/` are still passed through unchanged and forwarded to `fetch`
 * (so tests and standalone hosts can work without a resolver), but the
 * production wiring target is the full-URL form.
 */

/**
 * Fetch lifecycle state emitted per data source.
 *
 * - `pending`   — fetch in flight or not yet started
 * - `resolved`  — fetch succeeded and the extracted payload is non-empty
 * - `empty`     — fetch succeeded but the extracted payload is `[]`, `{}`,
 *                 `null`, or `undefined` (consumers render `onEmpty`)
 * - `error`     — fetch rejected, aborted on retry exhaustion, failed
 *                 allowlist, or threw during extraction
 */
export type DataSourceStatus = 'pending' | 'resolved' | 'error' | 'empty';

/**
 * Per-source public snapshot exposed via React context.
 */
export interface ResolvedDataSource {
  /** Extracted payload (post-`responsePath`), or `undefined` while pending / on error. */
  readonly data: unknown;
  readonly status: DataSourceStatus;
  /**
   * True after this source's request has been sent and before it settles.
   * Absent while the source is still waiting to start: a readiness check,
   * an explicit trigger, or a turn in the concurrency queue.
   */
  readonly dispatched?: boolean;
  /**
   * True while the declaration's `fetchWhen` preconditions all fail. The
   * source was deliberately not requested, so a consumer waiting for it to
   * settle can stop waiting: there is no data and none is coming until an
   * input the preconditions read changes.
   */
  readonly skipped?: boolean;
  /**
   * True while `data` is what the server resolved for the page and this
   * browser has not yet confirmed it would send the same request. The data
   * renders, but a consumer that decides something from it, such as a
   * decision evaluation, waits until the flag clears: the source's first run
   * either keeps the data or fetches its own.
   */
  readonly provisional?: boolean;
  readonly error?: Error;
  /**
   * When this source last produced data, as epoch milliseconds. Absent until
   * the first fetch succeeds.
   *
   * Set for `resolved` and `empty` alike -- learning that a query returns
   * nothing is a successful fetch -- and carried through a later `pending` or
   * `error`, because a refetch in flight or a failure does not change when the
   * data on screen was obtained. A cache hit reports when the entry was
   * written rather than when it was read, so age survives caching.
   *
   * This is what lets a bound component say how old its data is without the
   * host modeling freshness as a second data source.
   */
  readonly lastUpdatedAt?: number;
}

/**
 * One entry in DSM form state.
 *
 * A single-value control stores a string. A control that can hold more than one
 * selection stores the list, because the alternative — encoding it into one
 * string — makes the encoding part of the contract for everything downstream
 * that reads form state, and there is no encoding that round-trips every value.
 * A comma-joined list silently splits a value containing a comma, which for a
 * dimension-value filter (`"Washington, D.C."`) is a wrong query, not a
 * cosmetic problem.
 *
 * Interpolation, dependency scanning, and `formState.clear` are all defined
 * over both shapes. A whole-token `{{formState.key}}` expression keeps a list
 * as an array; a mixed template string comma-joins it.
 */
export type FormStateValue = string | readonly string[];

/** DSM-managed form state: one {@link FormStateValue} per key. */
export type FormState = Readonly<Record<string, FormStateValue>>;

/**
 * React context value returned by {@link useDataSourceManager}.
 *
 * `flowContext` is the host-supplied stable runtime object exposed as
 * `{{context.*}}` during interpolation. It is exposed on the hook so the
 * binding/action resolvers (DB-1.4) build their interpolation root from
 * the same shape DSM uses internally for source `query` execution
 * (`{ context: flowContext, formState, ...sources }`). Without this,
 * `{{context.foo}}` would mean different things in `dataSources[*].query`
 * vs `_bind.value` — see DB-1.4 review comment thread on PR #2366.
 */
export interface DataSourceManagerContextValue {
  readonly sources: Readonly<Record<string, ResolvedDataSource>>;
  readonly getSource: (name: string) => ResolvedDataSource | undefined;
  /**
   * Return the current authored declaration for `name`.
   *
   * Consumers that need request metadata while a source is still pending
   * (for example a repeater reserving the requested result capacity) cannot
   * derive it from the resolved snapshot alone.
   */
  readonly getDeclaration?: (name: string) => DataSourceDeclaration | undefined;
  readonly formState: FormState;
  /**
   * Live read of the latest formState, including writes made earlier in the
   * same synchronous call stack. `formState` is a render-time snapshot, so
   * handlers that run several writes and reads on one event (action lists)
   * use this instead. Optional so hosts without it fall back to `formState`.
   */
  readonly getFormState?: () => FormState;
  readonly updateFormState: (key: string, value: FormStateValue) => void;
  /**
   * Write several form-state keys as one snapshot and refetch once.
   * Optional so callers can fall back to {@link updateFormState}.
   */
  readonly updateFormStateMany?: (
    entries: Readonly<Record<string, string>>,
  ) => void;
  readonly flowContext: Readonly<Record<string, unknown>>;
  /**
   * Force re-run `name`. When `cascade` is true, also re-runs transitive
   * declared dependencies and dependents (each once, Kahn order).
   * Unknown names are a no-op.
   *
   * `body` overrides the declaration's `body` for this run only, and applies
   * to `name` alone — cascaded sources always use their own declarations. It
   * lets a host generate against a payload it computes at call time without
   * republishing `dataSources`, which would otherwise re-derive the source
   * graph and refetch every sibling. The override participates in the cache
   * key, so two payloads never share a cached result.
   *
   * @remarks
   * The returned promise settles when the run finishes — including when a
   * source ends in `error` or stays `pending` (e.g. unresolved form tokens).
   * Fetch failures are reported on `sources[name].status` / `.error`, not as
   * promise rejections, so cascade can continue and UI binds one channel.
   */
  readonly refetchSource: (
    name: string,
    opts?: { readonly cascade?: boolean; readonly body?: unknown },
  ) => Promise<void>;
  /**
   * Force re-run every declared source once in topological order.
   *
   * @remarks
   * Same settlement contract as {@link DataSourceManagerContextValue.refetchSource}:
   * the promise resolves when the walk completes; per-source outcomes live on
   * `sources[*].status` / `.error`, not on thrown rejections.
   */
  readonly refetchAll: () => Promise<void>;
  /**
   * Force re-run just `names`, grouped into dependency levels so independent
   * sources run concurrently (up to `concurrency`, default 5) while a dependent
   * still waits for the source whose resolved data it interpolates.
   *
   * Prefer this over {@link DataSourceManagerContextValue.refetchAll} when the
   * host knows which subset it wants re-queried: `refetchAll` also re-runs
   * option-list sources feeding authoring controls and forces
   * `trigger: 'explicit'` inference sources to fire against their declared
   * body. Unknown names are dropped; dependencies outside `names` are not
   * pulled in and keep whatever they last resolved to.
   *
   * @remarks
   * Same settlement contract as {@link DataSourceManagerContextValue.refetchSource}:
   * the promise resolves when the walk completes; per-source outcomes live on
   * `sources[*].status` / `.error`, not on thrown rejections.
   */
  readonly refetchMany: (
    names: readonly string[],
    opts?: {
      readonly concurrency?: number;
      /**
       * Also re-run every source the named ones transitively depend on, before
       * them. Off by default: a caller that enumerated its own subset has
       * already decided what to touch, and widening it would re-run sources it
       * left alone on purpose.
       */
      readonly includeDependencies?: boolean;
    },
  ) => Promise<void>;
  /**
   * Drop the last TTL cache entry for `name` without fetching or changing
   * status. Unknown / never-fetched / non-TTL-cached names are a no-op.
   *
   * @remarks
   * Does not clear `liveDataRef` or flip UI status — resolved data may remain
   * visible until the next fetch. For admin hard refresh, call
   * `refetchSource(name, { cascade: true })` rather than `invalidate` alone.
   */
  readonly invalidate: (name: string) => void;
  /**
   * Names of declared sources carrying any of `tags`, in declaration order.
   *
   * Exposed alongside the tag-scoped operations rather than only behind them,
   * because a host that wants to report what a refresh will touch -- or to
   * apply an operation the manager does not offer -- otherwise has to
   * re-derive the grouping from declarations it can no longer see.
   */
  readonly namesByTags: (tags: readonly string[]) => readonly string[];
  /**
   * Re-run every source carrying any of `tags`, with
   * {@link DataSourceManagerContextValue.refetchMany}'s level-grouped
   * concurrency and settlement contract. No matching tags is a no-op.
   */
  readonly refetchByTags: (
    tags: readonly string[],
    opts?: {
      readonly concurrency?: number;
      /**
       * Also re-run the dependencies of the tagged sources, before them.
       *
       * Worth considering for any tag-selected refresh, because selecting by tag
       * means not enumerating the set -- so the caller cannot know whether it is
       * closed under `dependsOn`. Without this, a tagged source whose dependency
       * falls outside the tag re-runs against that dependency's last value and
       * still reports success.
       *
       * Opt-in rather than the default because whether a dependency should
       * re-run depends on what it is for, and tags are matched here, never
       * interpreted. One feeding an authoring control wants to be left alone;
       * one feeding a request body wants to re-run.
       */
      readonly includeDependencies?: boolean;
    },
  ) => Promise<void>;
  /**
   * Drop cached entries for every source carrying any of `tags`, with
   * {@link DataSourceManagerContextValue.invalidate}'s semantics per source:
   * no fetch, no status change.
   */
  readonly invalidateByTags: (tags: readonly string[]) => void;
  /**
   * Register a reporting declaration owned by a rendered node. Nodes that
   * pass the same id share one fetch. `scope` is that node's published
   * scope; the declaration is interpolated against it, not the manager's.
   */
  readonly registerNodeSource?: (
    id: string,
    declaration: ReportingModelDataSourceDeclaration,
    scope: Readonly<Record<string, unknown>>,
  ) => void;
  /** Drop one registration. The source leaves the graph when the last node does. */
  readonly unregisterNodeSource?: (id: string) => void;
}

import type { CanvasTopologySource } from '../ce-editor/topology/canvas-topology-provider';
import type {
  ReportingOrderClause,
  ReportingQueryFilter,
  ReportingQueryHaving,
} from '../utils/data-sources/reporting-query-types';

export type DataSourceKind = 'http' | 'reportingModel' | 'inference';

/**
 * One path that must be present on an already-resolved source before fetch.
 */
export interface ReadyWhenClause {
  readonly source: string;
  readonly path: string;
}

/**
 * When an `inference` source should fetch.
 *
 * - `auto` (default) — fetch with the normal DSM lifecycle
 * - `explicit` — do not auto-fetch; stay `pending` until a host calls
 *   {@link DataSourceManagerContextValue.refetchSource} /
 *   {@link DataSourceManagerContextValue.refetchAll}
 */
export type DataSourceTrigger = 'auto' | 'explicit';

/** A scalar a fetch precondition compares against. */
export type FetchPreconditionValue = string | number | boolean;

/**
 * One check on a raw page input that decides whether a source is fetched.
 *
 * `source` is a raw input root: `context` (host-supplied facts such as
 * feature flags) or `formState`. It is never another data source and never a
 * decision outcome, so a precondition is known before any fetch or
 * evaluation runs. `path` reads into the root with dots and `[n]` indexes.
 * The operators match the business rules service's predicate allowlist, with
 * the same unknown-input rule: a missing value satisfies only `isNull`, so a
 * negation never fires on input nobody supplied.
 */
export type FetchPrecondition = {
  readonly source: 'context' | 'formState';
  readonly path: string;
} & (
  | {
      readonly op: 'eq' | 'neq';
      readonly value: FetchPreconditionValue;
    }
  | {
      readonly op: 'in' | 'notIn';
      readonly values: readonly FetchPreconditionValue[];
    }
  | { readonly op: 'isNull' | 'isNotNull' }
);

/**
 * Fields shared by every `dataSources[<name>]` entry.
 */
export interface DataSourceDeclarationBase {
  /** Names of sibling sources that must resolve first. Cycles throw at init. */
  readonly dependsOn?: readonly string[];
  /**
   * How to narrow the response JSON:
   *   - dot-path            — `'data'`, `'data.organizations'`
   *   - facet map           — `'_facets.<key>'`       → `{ name, slug, count }[]`
   *   - filter-count map    — `'_filterCounts.<key>'` → `{ name, slug, count }[]`
   * Omitting `responsePath` returns the raw JSON.
   */
  readonly responsePath?: string;
  readonly cache?: { readonly ttlMs: number };
  readonly errorStrategy?: DataSourceErrorStrategy;
  readonly onEmpty?: DataSourceFallbackContent;
  /**
   * Offerings `/v1/offerings/search` only. When `true`, the response
   * coercer partitions rows tagged `related: true` into a `related.data`
   * bucket (pair with a second repeater at `iteratorPath: 'related.data'`).
   * Omitted / `false` keeps every row flat in `data[]` with its `related`
   * flag intact — the default the single-list Editorial Listings viewMore
   * repeater relies on to count all fetched rows.
   */
  readonly bucketRelated?: boolean;
  /**
   * Opaque labels a host groups sources by, so it can refresh a subset without
   * the manager knowing what the subset means.
   *
   * The manager never interprets a tag. It matches strings, which is what keeps
   * a host's vocabulary -- `'chart'`, `'options'`, `'freshness'` -- out of here.
   * The alternative a host reaches for otherwise is classifying sources by
   * `kind` or by a naming convention in their ids, and both make the manager's
   * shape and the host's intent the same thing.
   */
  readonly tags?: readonly string[];
  /**
   * Wait until each path on an already-resolved source is present, then fetch.
   *
   * Each named source is an execution dependency, including when `dependsOn`
   * does not repeat it. The manager runs that source first and re-runs this
   * declaration when it settles. The check itself runs after those sources
   * settle and before the request is sent. While any path is absent the
   * source stays `pending`: no fetch, no error.
   *
   * Present means not `null` and not `undefined`. `''`, `0`, and `false`
   * are present. Unresolved-template checks treat `''` as not ready; this
   * field does not, and the two checks stay separate.
   */
  readonly readyWhen?: ReadyWhenClause | readonly ReadyWhenClause[];
  /**
   * Fetch only when at least one precondition holds. While none does, the
   * source is never requested: it stays `pending` with `skipped: true` and
   * no data, and a `formState` write the preconditions read re-checks it.
   * Absent or empty means the source fetches as it always has.
   */
  readonly fetchWhen?: readonly FetchPrecondition[];
}

/**
 * Default HTTP fetch declaration (`kind` omitted or `'http'`).
 *
 * String-valued fields may contain `{{…}}` template runs resolved at fetch
 * time against `{ context: flowContext, formState, ...resolvedSources }`.
 */
export interface HttpDataSourceDeclaration extends DataSourceDeclarationBase {
  readonly kind?: 'http';
  /**
   * Target endpoint. For DB-1.3 this is a full URL (or an already-resolved
   * path). DB-1.5 will add path→baseUrl resolution; DSM itself does not
   * prepend any base.
   */
  readonly endpoint: string;
  readonly method: 'GET' | 'POST';
  readonly headers?: Record<string, string>;
  readonly body?: unknown;
  readonly query?: Record<string, string | readonly string[]>;
}

/**
 * A form value copied into the query as a predicate or a string token.
 * Entries are emitted in form-state key order, one per participating key,
 * so two keys bound to the same field produce two predicates.
 */
export interface ReportingBoundFilter {
  readonly formKey: string;
  readonly field: string;
  readonly role: 'filter' | 'context';
  /**
   * Replaces the default predicate (`eq` for a scalar, `in` for a list)
   * when it fits the value: `in` / `not_in` wrap a scalar in a one-item
   * list, and a list value keeps `in` under a scalar operator.
   */
  readonly op?: ReportingQueryFilter['op'];
}

/**
 * The first non-empty form value among `formKeys` is the sequence's current
 * member. Anchors are exposed as `{{sequences.<sequenceId>.self|prior|priorPrior}}`.
 * With no current member those tokens resolve to nothing: context entries
 * that read them are omitted, filters whose value reads them are dropped,
 * and the request is still sent.
 */
export interface ReportingSequenceBinding {
  readonly sequenceId: string;
  readonly formKeys: readonly string[];
  /**
   * Context entries replaced by resolved anchors. When no current member
   * resolves, authored context at these keys remains unchanged.
   */
  readonly contextKeys?: {
    readonly self?: string;
    readonly prior?: string;
    readonly priorPrior?: string;
  };
  /**
   * When the sequence has a current member, replace `in` filters on this
   * field with the three anchors. Without a current member the authored
   * values are left as written.
   */
  readonly rewriteInField?: string;
}

/**
 * Picks a metric or dimension name from a form value. The last
 * participating non-empty value among `formKeys`, in form-state order,
 * is looked up in `valueMap`; a hit replaces every entry of `target` that
 * is one of the map's names. An unmapped value leaves the query as written.
 */
export interface ReportingValueSelection {
  readonly formKeys: readonly string[];
  readonly target: 'metrics' | 'dimensions';
  readonly valueMap: Readonly<Record<string, string>>;
  /** Also rename matching `order[].field` entries. */
  readonly renameOrder?: boolean;
}

/**
 * Semantic-model structured query executed via the reporting BFF/service.
 */
export interface ReportingModelDataSourceDeclaration
  extends DataSourceDeclarationBase {
  readonly kind: 'reportingModel';
  readonly modelId: string;
  readonly metrics: readonly string[];
  readonly dimensions?: readonly string[];
  readonly segments?: readonly string[];
  readonly filters?: readonly ReportingQueryFilter[];
  readonly having?: readonly ReportingQueryHaving[];
  readonly order?: readonly ReportingOrderClause[];
  readonly limit?: number;
  /** Token context; values may contain `{{formState.*}}` / `{{context.*}}`. */
  readonly context?: Readonly<Record<string, string | number>>;
  /**
   * Form values folded into filters or context at resolution time. An
   * authored filter keeps its field over a bound one; a bound context
   * token replaces an authored entry of the same name.
   */
  readonly boundFilters?: readonly ReportingBoundFilter[];
  /**
   * Which form keys participate in `boundFilters`, `sequenceBindings`, and
   * `valueSelections`. `null` / omitted = every key, `[]` = none, a list =
   * those keys. A string is a template (for example `{{scope.<key>}}`)
   * resolved to the same three shapes; any other result keeps the source
   * pending and warns once.
   */
  readonly applicableFormKeys?: readonly string[] | string | null;
  /** Drop bound `eq` filters on these fields. Authored filters are kept. */
  readonly excludeEqFields?: readonly string[];
  readonly sequenceBindings?: readonly ReportingSequenceBinding[];
  readonly valueSelections?: readonly ReportingValueSelection[];
  /**
   * Form values this declaration reads in place of page form state,
   * applied after `applicableFormKeys`. `null` removes the key.
   */
  readonly formOverrides?: Readonly<Record<string, FormStateValue | null>>;
}

/**
 * Cost-bearing narrative / insight generation via a host-supplied inference URL.
 *
 * The POST body is opaque to CE — the host owns shape and any pre-reduction
 * (A6). Values may contain `{{…}}` templates resolved at fetch time.
 */
export interface InferenceDataSourceDeclaration
  extends DataSourceDeclarationBase {
  readonly kind: 'inference';
  /**
   * Opaque POST body; may contain `{{…}}` templates. Host owns shape (A6).
   *
   * Omitting it on a `trigger: 'auto'` source posts an empty body on mount and
   * still bills a generation. Authors should either supply a `body` or declare
   * the source `explicit`.
   */
  readonly body?: unknown;
  readonly headers?: Record<string, string>;
  /**
   * `auto` (default) — fetch with the normal DSM lifecycle.
   * `explicit` — do not auto-fetch; stay `pending` until the host calls
   * {@link DataSourceManagerContextValue.refetchSource} or
   * {@link DataSourceManagerContextValue.refetchAll}. Mount and formState
   * paths never fetch explicit sources.
   */
  readonly trigger?: DataSourceTrigger;
}

/**
 * Source declarations keyed by `kind`.
 *
 * The union below is this map's values, so `decl.kind === 'inference'` still
 * narrows to the inference declaration. A `kind: string` arm cannot sit in
 * that union: it overlaps every built-in discriminant, and after the check
 * `body` is no longer known to exist.
 *
 * A host that registers another source type augments this interface instead
 * of editing it. The new member is another discriminant, and the built-in
 * three stay as precise as they are here.
 *
 * `rollupTypes` emits this interface on the package entry. A host augments
 * that module, then registers a descriptor on the exported `dataSourceTypes`
 * registry. Inside this repo the interface is still declared here, which is
 * the module the registry test augments.
 *
 * @example
 * declare module '@highereducation/dynamic-experience' {
 *   interface DataSourceKindMap {
 *     graphql: GraphqlDecl;
 *   }
 * }
 * import { dataSourceTypes, type DataSourceTypeDescriptor } from
 *   '@highereducation/dynamic-experience';
 * dataSourceTypes.register(graphqlSourceType);
 */
export interface DataSourceKindMap {
  http: HttpDataSourceDeclaration;
  reportingModel: ReportingModelDataSourceDeclaration;
  inference: InferenceDataSourceDeclaration;
}

export type DataSourceDeclaration = DataSourceKindMap[keyof DataSourceKindMap];

/**
 * Optional per-declaration overrides for fetch timeout / retries.
 *
 * Kind defaults when omitted:
 * - `http` / `reportingModel` — timeout **10_000** ms, retries **2**
 * - `inference` — timeout **30_000** ms, retries **0** (cost-safe generation defaults)
 */
export interface DataSourceErrorStrategy {
  /** Per-attempt timeout in milliseconds. See interface JSDoc for kind defaults. */
  readonly timeout?: number;
  /**
   * Number of additional retry attempts after the initial try.
   * See interface JSDoc for kind defaults.
   *
   * On an `inference` source every attempt is a billed generation, so raising
   * this above the cost-safe default multiplies spend per render.
   */
  readonly retries?: number;
  readonly onError?: 'showFallback';
  readonly fallback?: DataSourceFallbackContent;
}

export interface DataSourceFallbackContent {
  readonly heading: string;
  readonly body: string;
  readonly ctaLabel?: string;
  readonly ctaAction?: string;
}

/**
 * Props accepted by {@link DataSourceManagerProvider}.
 */
export interface DataSourceManagerProps {
  /**
   * Source declarations reconciled by name. Rewriting one declaration
   * automatically re-runs only that source; dependents keep their current
   * resolved snapshots. Hosts whose dependents interpolate `{{<name>.*}}`
   * should use `refetchSource(name, { cascade: true })` or `refetchMany` when
   * they need the dependent snapshots refreshed too.
   */
  readonly dataSources: Record<string, DataSourceDeclaration>;
  /** Stable runtime context exposed as `{{context.*}}` during interpolation. */
  readonly flowContext: Record<string, unknown>;
  /**
   * Host hook invoked after every `updateFormState` state commit. DB-1.5
   * (ConfigurableStep) translates form state into DE `flowState` via this
   * callback; standalone hosts may omit it.
   */
  readonly onFormStateChange?: (next: FormState) => void;
  /**
   * Initial `formState`. Useful when the host wants a data source to run
   * once with pre-seeded form values (e.g. query-param hydration).
   */
  readonly initialFormState?: FormState;
  /**
   * formState keys interpolated into query and body, but withheld from
   * the endpoint. Endpoint substitution is not URL-encoded.
   */
  readonly endpointExcludedFormKeys?: ReadonlySet<string>;
  /**
   * Origins that absolute-URL endpoints in `dataSources` are allowed
   * to fetch from. **Required for absolute URL declarations.** When
   * omitted/empty, only path-only endpoints (`/v1/...`) are accepted
   * and any absolute URL is rejected at runtime — even if its path
   * matches the data-source allowlist (review #1 re-review,
   * R. Vazquez). DB-1.5 will derive this from the host's
   * `apiConfig`; standalone hosts (and tests) supply it directly.
   *
   * Entries may be passed as bare origins
   * (`https://api.example.com`) or full URLs whose origin is
   * extracted (`https://api.example.com/v1/search` →
   * `https://api.example.com`).
   */
  readonly trustedOrigins?: readonly string[];
  /**
   * Canonical runtime tenant from trusted host/session context. Forwarded
   * to the endpoint-policy check. Allowlist-scoped sources (Product API)
   * deny when this is omitted or not eligible.
   */
  readonly tenantId?: string;
  /**
   * Optional last-mile transform applied to every resolved request
   * after interpolation, before the allowlist + origin checks.
   * Forwarded verbatim to {@link fetchDataSource}'s `coerceRequestBody`
   * option. Use this to inject service-shaped runtime rules (today:
   * Mobius numeric-id coercion in `body.criteria.*`) without bleeding
   * service knowledge into DSM itself. The bridge (ConfigurableStep)
   * builds the transform from `apiConfig`; standalone hosts may omit
   * this prop.
   */
  readonly coerceRequestBody?: (
    req: import('../utils/data-sources/fetchDataSource').InterpolatedRequest,
  ) => import('../utils/data-sources/fetchDataSource').InterpolatedRequest;
  /**
   * Optional last-mile transform applied to every successful response's
   * raw JSON after fetch, before `responsePath` extraction. Forwarded
   * verbatim to {@link fetchDataSource}'s `coerceResponse` option.
   * Symmetric to {@link coerceRequestBody}: use it to reshape a
   * service-shaped payload into the contract the DSL/UI expects (today:
   * normalizing Offerings `/match` bucket items into the flat `Result`
   * card contract) without bleeding service knowledge into DSM. The
   * bridge (ConfigurableStep) builds it from `apiConfig`; standalone
   * hosts may omit this prop.
   */
  readonly coerceResponse?: (
    res: unknown,
    req: import('../utils/data-sources/fetchDataSource').InterpolatedRequest,
    decl?: DataSourceDeclaration,
  ) => unknown;
  /**
   * Full URL for `POST` structured reporting queries when a declaration uses
   * `kind: 'reportingModel'`. Typically `apiConfig.reportingApiUrl` +
   * `/api/reporting/v1/query`. Required for reportingModel sources.
   */
  readonly reportingQueryUrl?: string;
  /**
   * Full URL for `POST` inference / narrative generation when a declaration
   * uses `kind: 'inference'`. Typically the host BFF origin +
   * `/api/ai/insight`. Required for inference sources.
   */
  readonly inferenceUrl?: string;
  /**
   * Host canvas used to project `{{topology.siblings.<id>.*}}`.
   * Omitted means the projection has no siblings, so a sibling read errors.
   */
  readonly topologySource?: CanvasTopologySource;
  /**
   * Opt-in cap on fetches in flight, per source kind.
   * Omitted means no cap. A permit is released when that fetch settles.
   */
  readonly maxConcurrentByKind?: Partial<
    Record<keyof DataSourceKindMap, number>
  >;
  /**
   * Sources the server already resolved for this page. A source's first run
   * uses its entry instead of fetching when it would send the request the
   * server sent. Omitted reads the nearest `ServerResolvedSourcesProvider`.
   */
  readonly serverResolvedSources?: import('../utils/data-sources/server/serverSeed').ServerResolvedSources;
  readonly children: React.ReactNode;
}

/**
 * Thrown synchronously at provider init when `dataSources` contains a
 * dependency cycle. ADR-10 requires a loud failure — the config is
 * invalid and no source will ever resolve.
 */
export class DataSourceCycleError extends Error {
  public readonly cycle: readonly string[];

  public constructor(cycle: readonly string[]) {
    super(
      `DataSource dependency cycle detected: ${cycle.join(' -> ')} -> ${
        cycle[0] ?? ''
      }`,
    );
    this.name = 'DataSourceCycleError';
    this.cycle = cycle;
    Object.setPrototypeOf(this, DataSourceCycleError.prototype);
  }
}

/**
 * Surfaced on `ResolvedDataSource.error` when `dependsOn` or `readyWhen`
 * names an undeclared source. The DataSourceManager seeds the offending
 * source as
 * `status: 'error'` and continues resolving every other source.
 */
export class DataSourceMissingDependencyError extends Error {
  public readonly source: string;
  public readonly missing: string;

  public constructor(source: string, missing: string) {
    super(
      `DataSource "${source}" depends on "${missing}", but no data source with that name exists.`,
    );
    this.name = 'DataSourceMissingDependencyError';
    this.source = source;
    this.missing = missing;
    Object.setPrototypeOf(this, DataSourceMissingDependencyError.prototype);
  }
}

/**
 * Surfaced on `ResolvedDataSource.error` when a declaration's `fetchWhen`
 * holds a precondition that is not allowed, such as one that reads a
 * decision outcome. The source is never requested, and every other source
 * resolves as usual.
 */
export class DataSourcePreconditionRefusedError extends Error {
  public readonly source: string;
  public readonly reason: string;

  public constructor(source: string, reason: string) {
    super(
      `DataSource "${source}" is not fetched: its fetchWhen is refused. ${reason}`,
    );
    this.name = 'DataSourcePreconditionRefusedError';
    this.source = source;
    this.reason = reason;
    Object.setPrototypeOf(this, DataSourcePreconditionRefusedError.prototype);
  }
}

/**
 * Runtime guard surfaced via `ResolvedDataSource.error` when the DB-1.1
 * allowlist check rejects a request endpoint. Named with the
 * `Endpoint` qualifier to avoid collision with the URL-resolution
 * variant (`DataSourceNotAllowedError`) exported from
 * `config/allowed-data-sources`, which carries richer
 * `(service, path, method, message)` context used at resolution time.
 */
export class DataSourceEndpointNotAllowedError extends Error {
  public readonly endpoint: string;
  public readonly method: string;

  public constructor(endpoint: string, method: string) {
    super(
      `DataSource endpoint "${method} ${endpoint}" is not permitted by the data source allowlist.`,
    );
    this.name = 'DataSourceEndpointNotAllowedError';
    this.endpoint = endpoint;
    this.method = method;
    Object.setPrototypeOf(this, DataSourceEndpointNotAllowedError.prototype);
  }
}

/**
 * Thrown when the response cannot be parsed / extracted into the shape
 * `responsePath` implies (e.g. `_facets.<key>` against a non-object
 * response). Classified as `status: 'error'` on the source.
 */
export class DataSourceExtractionError extends Error {
  public readonly responsePath?: string;

  public constructor(message: string, responsePath?: string) {
    super(message);
    this.name = 'DataSourceExtractionError';
    this.responsePath = responsePath;
    Object.setPrototypeOf(this, DataSourceExtractionError.prototype);
  }
}

/**
 * Thrown when fetch returns a non-2xx response and retries are exhausted.
 */
export class DataSourceHttpError extends Error {
  public readonly status: number;
  public readonly endpoint: string;
  public readonly method: string;
  /**
   * The upstream service's machine-readable error code, kept beside `message`
   * rather than folded into it.
   *
   * A host renders `message` to whoever is looking at the screen, and a code
   * like `INVALID_METRIC` is not for them -- it is for whoever reads the logs.
   * Concatenating the two put it in front of the wrong audience and made the
   * text differ from the same error presented through a host's own formatter,
   * which strips codes deliberately. Separate fields let both readers have what
   * they need.
   */
  public readonly code?: string;

  public constructor(
    status: number,
    endpoint: string,
    method: string,
    message?: string,
    code?: string,
  ) {
    super(
      message ??
        `DataSource "${method} ${endpoint}" responded with HTTP ${status}`,
    );
    this.name = 'DataSourceHttpError';
    this.status = status;
    this.endpoint = endpoint;
    this.method = method;
    if (code !== undefined) this.code = code;
    Object.setPrototypeOf(this, DataSourceHttpError.prototype);
  }
}

/**
 * A fetch that never produced a response: DNS failure, refused connection,
 * TLS error, or a timeout that exhausted its retries.
 *
 * Separate from {@link DataSourceHttpError} because there is no status to
 * report -- the service did not answer. It carries the same `code` so a host
 * can recognize an outage and substitute its own copy through one branch,
 * rather than showing a reader the endpoint and method, which is a detail for
 * an operator.
 *
 * Deliberately not raised for a cancelled fetch or for a response that arrived
 * and could not be read. A cancellation is the host's own decision, and a
 * malformed body is a contract problem; calling either an outage would put
 * "temporarily unavailable" in front of a reader for something else entirely.
 */
export class DataSourceTransportError extends Error {
  public readonly endpoint: string;
  public readonly method: string;
  public readonly code?: string;

  public constructor(
    endpoint: string,
    method: string,
    message?: string,
    code?: string,
  ) {
    super(
      message ??
        (code !== undefined
          ? 'The data source could not be reached.'
          : `DataSource "${method} ${endpoint}" could not be reached.`),
    );
    this.name = 'DataSourceTransportError';
    this.endpoint = endpoint;
    this.method = method;
    if (code !== undefined) this.code = code;
    Object.setPrototypeOf(this, DataSourceTransportError.prototype);
  }
}

/**
 * Thrown when a declaration carries an unsupported `kind` value.
 */
export class DataSourceUnknownKindError extends Error {
  public readonly kind: string;

  public constructor(kind: string) {
    super(`DataSource kind "${kind}" is not supported.`);
    this.name = 'DataSourceUnknownKindError';
    this.kind = kind;
    Object.setPrototypeOf(this, DataSourceUnknownKindError.prototype);
  }
}

/**
 * Thrown when `kind: 'reportingModel'` is used but the host did not supply
 * {@link DataSourceManagerProps.reportingQueryUrl}.
 */
export class DataSourceReportingNotConfiguredError extends Error {
  public constructor() {
    super(
      'DataSource reportingModel fetch requires reportingQueryUrl on DataSourceManagerProvider.',
    );
    this.name = 'DataSourceReportingNotConfiguredError';
    Object.setPrototypeOf(
      this,
      DataSourceReportingNotConfiguredError.prototype,
    );
  }
}

/**
 * Thrown when `kind: 'inference'` is used but the host did not supply
 * {@link DataSourceManagerProps.inferenceUrl}.
 */
export class DataSourceInferenceNotConfiguredError extends Error {
  public constructor() {
    super(
      'DataSource inference fetch requires inferenceUrl on DataSourceManagerProvider.',
    );
    this.name = 'DataSourceInferenceNotConfiguredError';
    Object.setPrototypeOf(
      this,
      DataSourceInferenceNotConfiguredError.prototype,
    );
  }
}
